import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * TTS 차단 응답(HTTP 422 + body code 1031/1032) 을 받았을 때 띄우는 전역 단일 다이얼로그.
 *
 * 단일 store 인 이유: 같은 텍스트로 generate / generate-as-asset / voice-preview 가
 * 동시에 흐를 수 있고, BE 가 모두 차단 응답하면 caller 마다 dialog 가 중복 mount 된다.
 * → store 가 dedupe 의 책임자. open=true 인 상태에서 재호출은 무시.
 *
 * 위치 — `feature-blocked-dialog-store.ts` 와 동일하게 `shared/store/` 에 둔다.
 * 이유: `shared/apis/tts/*` API wrapper 가 store 를 트리거해야 하는데, FSD 단방향
 * (shared → features 금지) 위반을 피하려면 store 가 shared 에 있어야 한다.
 */
export type TtsBlockedSource =
  | 'generate'
  | 'generate-as-asset'
  | 'voice-preview';

export interface TtsBlockedDialogState {
  open: boolean;
  /** 어느 mutation 에서 차단됐는지. Amplitude property 용. UI 노출 X. */
  source?: TtsBlockedSource;
  /** BE traceId. dev console 로그 보조 + Amplitude property 용. UI 노출 X. */
  traceId?: string;
}

export interface TtsBlockedDialogActions {
  /**
   * 차단 응답을 받은 caller(또는 API wrapper) 가 호출.
   * 이미 open 이면 noop (dedupe).
   * @returns 실제로 dialog 를 열었는지 — Amplitude 중복 발사 회피 등에 활용.
   */
  openBlocked: (payload: {
    source: TtsBlockedSource;
    traceId?: string;
  }) => boolean;
  close: () => void;
  reset: () => void;
}

const INITIAL: TtsBlockedDialogState = {
  open: false,
  source: undefined,
  traceId: undefined,
};

const isDev = process.env.NEXT_PUBLIC_MODE !== 'production';

const storeCreator = (
  set: (
    partial:
      | (TtsBlockedDialogState & TtsBlockedDialogActions)
      | Partial<TtsBlockedDialogState & TtsBlockedDialogActions>
      | ((
          state: TtsBlockedDialogState & TtsBlockedDialogActions,
        ) =>
          | (TtsBlockedDialogState & TtsBlockedDialogActions)
          | Partial<TtsBlockedDialogState & TtsBlockedDialogActions>),
  ) => void,
  get: () => TtsBlockedDialogState & TtsBlockedDialogActions,
): TtsBlockedDialogState & TtsBlockedDialogActions => ({
  ...INITIAL,
  openBlocked: ({ source, traceId }) => {
    if (get().open) return false;
    set({ open: true, source, traceId });
    return true;
  },
  close: () => set({ open: false }),
  reset: () => set(INITIAL),
});

export const useTtsBlockedDialogStore = create<
  TtsBlockedDialogState & TtsBlockedDialogActions
>()(
  isDev ? devtools(storeCreator, { name: 'tts-blocked-dialog' }) : storeCreator,
);
